import {
  Component,
  AfterViewInit,
  OnDestroy,
  PLATFORM_ID,
  Inject,
  Injector,
  ChangeDetectionStrategy,
  NgZone,
} from '@angular/core';
import {
   NavigationEnd,
   NavigationStart,
   Params,
   Router,
   RouterOutlet,
} from '@angular/router';

import { AuthService } from './shared/services/auth.service';

import { ActivatedRoute } from '@angular/router';
import { distinctUntilChanged, Subject } from 'rxjs';
import { SubscriptionService } from './shared/services/subscription.service';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap/modal';
import { AnalyticsUtils } from 'src/app/shared/services/analytics-utils';
import { DataLayerService } from './shared/services/data-layer.service';
import { CouponService } from './shared/services/coupon.service';
import {
   setCountryCode,
   setCountryCodeResolutionComplete,
   setCountryCodeResolutionPending,
} from './stores/global/global.actions';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { loadWatchlistSymbols } from './stores/watchlist-symbols/watchlist-symbols.actions';
import { isPlatformBrowser } from '@angular/common';
// import { QuestionnaireModalComponent } from './cabinet/questionnaire-modal/questionnaire-modal.component';
import { Meta } from '@angular/platform-browser';
// import { RibaModalService } from './shared/services/riba-modal.service';
import { ClaimOneYearFreePopupService } from './shared/services/claim-one-year-free-popup.service';
// import { ClaimOneYearFreePopupComponent } from './shared/components/claim-one-year-free-popup/claim-one-year-free-popup.component';
import { canShowClaimOneYearFreeOffer, getClaimOfferIsSubscribed } from './utils/claim-offers';
import { shouldScheduleMobileClaimOneYearPopup } from './utils/mobile-claim-one-year-popup';
import { SEOService } from './shared/services/seo.service';
import { BranchSdkService } from './shared/services/branch-sdk.service';
import { UserCountryContextService } from './shared/services/user-country-context.service';
import { ReferrerService } from './shared/services/referrer.service';
import { LoaderComponent } from './core/components/loader/loader.component';
import { DownloadAppPromptComponent } from './core/components/download-app-prompt/download-app-prompt.component';
import { MobileSignupCtaComponent } from './core/components/mobile-signup-cta/mobile-signup-cta.component';
import type { FeaturebaseService } from './shared/services/featurebase.service';
import { getYearEndGrowthCampaignState } from './utils/year-end-growth-campaign';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
      RouterOutlet,
      LoaderComponent,
      DownloadAppPromptComponent,
      MobileSignupCtaComponent,
    ],
})
export class AppComponent implements AfterViewInit, OnDestroy {
   private unsubscribe$: Subject<void> = new Subject<void>();
   title = 'musaffa-platform';
   fireAuthUserLoaded = false;
   stripeSubscriptionStatus: any;
   record_id: string | null = '';
   module: string | null = '';
   isUnderMaintenance = false;
   isBrowser = false;
   private readonly claimOneYearFreeQueryParam = 'claim_offer';
   private readonly claimOneYearFreeQueryValue = '1_year_free';
   private isHandlingClaimOneYearFreeQueryParam = false;
   private featurebaseUrl = '/';
   private featurebaseServicePromise?: Promise<FeaturebaseService>;
   private mobileClaimOneYearPopupTimeout: ReturnType<typeof setTimeout> | null = null;
   private readonly yearEndGrowthCampaignPopupKey = 'yearEndGrowthCampaignModalShown';
   private yearEndGrowthCampaignPopupTimeout: ReturnType<typeof setTimeout> | null = null;
   constructor(
      private router: Router,
      public auth: AuthService,
      public subscriptionService: SubscriptionService,
      private toaster: ToastrService,
      private route: ActivatedRoute,
      private modalService: NgbModal,
      // public authFire: AngularFireAuth,
      private analyticsUtils: AnalyticsUtils,
      private activatedRoute: ActivatedRoute,
      private dataLayerService: DataLayerService,
      private couponService: CouponService,
      private store: Store,
      private metaService: Meta,
      private seoService: SEOService,
      // private ribaModalService: RibaModalService,
      private claimOneYearFreePopupService: ClaimOneYearFreePopupService,
      private branchSdkService: BranchSdkService,
      private userCountryContext: UserCountryContextService,
      private referrerService: ReferrerService,
      private injector: Injector,
      private ngZone: NgZone,
      @Inject(PLATFORM_ID) private platformId: Object
   ) {
      this.isBrowser = isPlatformBrowser(this.platformId);
      this.featurebaseUrl = router.url;

      this.auth.isLoggedIn$
         .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
         .subscribe((isLoggedIn) => {
            if (isLoggedIn) {
               void this.analyticsUtils.identifyUser();
               void this.getFeaturebaseService()
                  .then((service) => {
                     service.handleRoute(this.featurebaseUrl);
                     return service.initializeForCurrentUser();
                  })
                  .catch((error) => console.error('Featurebase initialization failed', error));
            } else if (this.featurebaseServicePromise) {
               void this.featurebaseServicePromise.then((service) => service.shutdown());
            }
         });

      // this.isUnderMaintenance = true;
      if (this.isBrowser) {
         this.couponService.extractAndStoreCoupon();
         const url = new URL(window.location.href);
         const params = new URLSearchParams(url.search);

         this.module = params.get('module');
         this.record_id = params.get('record_id');

         if (this.hasBranchDeepLinkParams(params)) {
            this.initializeBranchDeepLinking();
         } else {
            this.scheduleAfterIdle(() => this.initializeBranchDeepLinking());
         }

         this.refreshSubscriptionAndPermissions();
      }

      router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
         if (val instanceof NavigationStart) {
            this.referrerService.recordNavigationStart(val.id);
            this.seoService.updateCanonicalUrl(this.getCanonicalUrl(val.url));
         }
         if (!isPlatformBrowser(this.platformId)) return;
         if (val instanceof NavigationStart) {
            // Set title and description for SEO only for pages that don't have specific metadata
            if (!val.url.includes('/stock-screener') && !val.url.includes('/etf-screener') && !val.url.includes('/halal-collection')) {
               this.seoService.updateTitle('Musaffa - Halal Stock Screening & Investment Platform');
               this.metaService.updateTag({
                 name: 'description',
                 content: 'Musaffa is your trusted platform for halal stock screening. Find Shariah-compliant stocks and ETFs with our comprehensive screening tools.'
               });
            }
            // const countryCode = localStorage.getItem('countryCode');
            // if (!countryCode) {
            //    this.apiService.getUserCountryCode().then((response) => {
            //       if (response?.data?.iso_code)
            //          localStorage.setItem(
            //             'countryCode',
            //             response.data.iso_code
            //          );
            //    });
            // }
         }
         if (val instanceof NavigationEnd) {
            this.featurebaseUrl = val.urlAfterRedirects;
            this.scheduleMobileClaimOneYearPopupIfNeeded(val.urlAfterRedirects);
            if (this.featurebaseServicePromise) {
               void this.featurebaseServicePromise.then((service) =>
                  service.handleRoute(val.urlAfterRedirects)
               );
            }
            void this.subscriptionService.setSubscriptionPlans().catch(() => {});

            if (
               !(
                  val.url.includes('authentication') ||
                  val.url.includes('pages')
               )
            ) {
               localStorage.setItem('redirectUrl', val.url);
            }
            if (
               !val.url.includes('/stock-screener') &&
               !val.url.includes('/etf-screener') &&
               !val.url.includes('/halal-collection')
            ) {
               if (isPlatformBrowser(this.platformId)) {
                  // Only scroll to top if there's no fragment in the URL
                  if (!val.url.includes('#')) {
                     setTimeout(() => {
                        window.scrollTo({
                           top: 0,
                           left: 0,
                           behavior: 'instant' as ScrollBehavior,
                        });
                     }, 100);
                  }
               }
            }

            // Show riba modal on all pages except stock and ETF pages
            // this.checkAndShowRibaModal(val.url);


         }
      });

      if (this.module) {
         void this.goToMusaffaPage(this.module, this.record_id);
      }

      
   }






   private getCanonicalUrl(url: string): string {
      const path = (url || '/').split(/[?#]/)[0] || '/';
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const canonicalPath = normalizedPath === '/' || normalizedPath.endsWith('/')
         ? normalizedPath
         : `${normalizedPath}/`;

      return `https://musaffa.com${canonicalPath}`;
   }

   // checkAndOpenDownloadPrompt() {
   //    if (this.isBrowser) {
   //       const hasOpenedPrompt = sessionStorage.getItem('hasOpenedPrompt');
   //       if (this.isSubscribed && !hasOpenedPrompt) {
   //          this.openPrompt();
   //          sessionStorage.setItem('hasOpenedPrompt', 'true');
   //       }
   //    }
   // }

   ngAfterViewInit(): void {
      // this.checkAndOpenDownloadPrompt();
      this.scheduleYearEndGrowthCampaignPopup();
   }

   ngOnDestroy() {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
      this.clearMobileClaimOneYearPopupTimeout();
      this.clearYearEndGrowthCampaignPopupTimeout();
   }

   private scheduleYearEndGrowthCampaignPopup(): void {
      if (
         !this.isBrowser ||
         sessionStorage.getItem(this.yearEndGrowthCampaignPopupKey) === 'true' ||
         !getYearEndGrowthCampaignState()
      ) {
         return;
      }

      this.yearEndGrowthCampaignPopupTimeout = setTimeout(() => {
         this.yearEndGrowthCampaignPopupTimeout = null;
         void this.openYearEndGrowthCampaignPopupWhenAvailable();
      }, 10000);
   }

   private async openYearEndGrowthCampaignPopupWhenAvailable(): Promise<void> {
      if (
         !this.isBrowser ||
         sessionStorage.getItem(this.yearEndGrowthCampaignPopupKey) === 'true' ||
         !getYearEndGrowthCampaignState()
      ) {
         return;
      }

      if (this.modalService.hasOpenModals()) {
         this.retryYearEndGrowthCampaignPopup();
         return;
      }

      try {
         const { YearEndGrowthCampaignPopupComponent } = await import(
            './shared/components/year-end-growth-campaign-popup/year-end-growth-campaign-popup.component'
         );

         if (
            sessionStorage.getItem(this.yearEndGrowthCampaignPopupKey) === 'true' ||
            !getYearEndGrowthCampaignState()
         ) {
            return;
         }

         if (this.modalService.hasOpenModals()) {
            this.retryYearEndGrowthCampaignPopup();
            return;
         }

         this.modalService.open(YearEndGrowthCampaignPopupComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: true,
            ariaLabelledBy: 'year-end-campaign-title',
            ariaDescribedBy: 'year-end-campaign-description',
            backdropClass:
               '!tw-bg-[oklch(32%_0_0)] !tw-opacity-[0.82]',
            windowClass: '!tw-w-screen !tw-pr-0',
            modalDialogClass:
               '!tw-my-4 !tw-ml-4 !tw-mr-0 !tw-min-h-[calc(100%-2rem)] !tw-w-[calc(100vw-2rem)] !tw-max-w-[35rem] sm:!tw-mx-auto [&_.modal-content]:!tw-overflow-visible [&_.modal-content]:!tw-rounded-[1.75rem] [&_.modal-content]:!tw-border-0 [&_.modal-content]:!tw-bg-transparent',
         });
         sessionStorage.setItem(this.yearEndGrowthCampaignPopupKey, 'true');
      } catch (error) {
         console.error('Unable to open the year-end growth campaign popup', error);
      }
   }

   private retryYearEndGrowthCampaignPopup(): void {
      this.yearEndGrowthCampaignPopupTimeout = setTimeout(() => {
         this.yearEndGrowthCampaignPopupTimeout = null;
         void this.openYearEndGrowthCampaignPopupWhenAvailable();
      }, 1000);
   }

   private clearYearEndGrowthCampaignPopupTimeout(): void {
      if (this.yearEndGrowthCampaignPopupTimeout) {
         clearTimeout(this.yearEndGrowthCampaignPopupTimeout);
         this.yearEndGrowthCampaignPopupTimeout = null;
      }
   }

   private scheduleMobileClaimOneYearPopupIfNeeded(url: string): void {
      if (!this.isBrowser) return;

      this.clearMobileClaimOneYearPopupTimeout();

      if (!this.shouldScheduleMobileClaimOneYearPopup(url)) {
         return;
      }

      this.mobileClaimOneYearPopupTimeout = setTimeout(() => {
         if (!this.shouldScheduleMobileClaimOneYearPopup(this.router.url)) {
            return;
         }

         this.auth.openAuthPopup(null, null, 'signUp', 'waitlist');
         this.referrerService.markPopupShown();
         this.mobileClaimOneYearPopupTimeout = null;
      }, 10000);
   }

   private shouldScheduleMobileClaimOneYearPopup(url: string): boolean {
      return shouldScheduleMobileClaimOneYearPopup({
         isMobileViewport: this.isMobileClaimPopupViewport(),
         isLoggedIn: this.auth.isLoggedIn(),
         isFirstTab: this.referrerService.isFirstTab(),
         hasNavigatedSinceInitialOpen: this.referrerService.hasNavigatedSinceInitialOpen(),
         popupAlreadyShown: this.referrerService.popupAlreadyShown(),
         url,
      });
   }

   private isMobileClaimPopupViewport(): boolean {
      return window.innerWidth < 768;
   }

   private clearMobileClaimOneYearPopupTimeout(): void {
      if (this.mobileClaimOneYearPopupTimeout) {
         clearTimeout(this.mobileClaimOneYearPopupTimeout);
         this.mobileClaimOneYearPopupTimeout = null;
      }
   }

   private scheduleAfterIdle(callback: () => void, delay = 5000): void {
      if (!this.isBrowser) return;

      const win = window as Window & {
         requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      };
      const idle = win.requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 2500));
      const run = () => window.setTimeout(() => idle(callback, { timeout: 5000 }), delay);

      if (document.readyState === 'complete') {
         run();
      } else {
         window.addEventListener('load', run, { once: true });
      }
   }

   private initializeBranchDeepLinking(): void {
      if (!this.isBrowser) return;

      this.branchSdkService
         .data((err: unknown, data: any) => {
            if (err) return;

            const module = data?.data_parsed?.$deeplink_path;
            const recordId = data?.data_parsed?.record_id;
            void this.goToMusaffaPage(module, recordId);
         })
         .catch((error: unknown) => console.error('Branch SDK failed to load', error));
   }

   private getFeaturebaseService(): Promise<FeaturebaseService> {
      this.featurebaseServicePromise ??= import(
         './shared/services/featurebase.service'
      ).then(({ FeaturebaseService }) => this.injector.get(FeaturebaseService));

      return this.featurebaseServicePromise;
   }

   private hasBranchDeepLinkParams(params: URLSearchParams): boolean {
      return Array.from(params.keys()).some(
         (key) =>
            key === 'link_click_id' ||
            key.startsWith('_branch') ||
            key.startsWith('~') ||
            key.startsWith('$')
      );
   }

   private async goToMusaffaPage(module: string, recordId: string | null): Promise<void> {
      const { MusaffaRedirectService } = await import(
         './shared/services/musaffa-redirect.service'
      );
      this.injector.get(MusaffaRedirectService).goToPage(module, recordId);
   }

   private refreshSubscriptionAndPermissions(): void {
      if (!this.isBrowser) return;

      void this.subscriptionService.setUserSubscriptionInfo();
   }

   private setCountryCodeIfNeeded(): void {
      const knownCountryCode = this.userCountryContext.getKnownCountryCode();
      if (knownCountryCode) {
         this.dispatchCountryCode(knownCountryCode);
         return;
      }

      this.store.dispatch(setCountryCodeResolutionPending());
      this.ngZone.runOutsideAngular(() => {
         void this.userCountryContext.resolveCountryCode()
            .then((countryCode) => {
               this.ngZone.run(() => this.dispatchCountryCode(countryCode));
            })
            .finally(() => {
               this.ngZone.run(() => this.store.dispatch(setCountryCodeResolutionComplete()));
            });
      });
   }

   private dispatchCountryCode(countryCode: string): void {
      if (countryCode !== 'UNKNOWN') {
         this.store.dispatch(setCountryCode({ countryCode }));
      }
   }

   // async logoutFireAuthAndSignInAnonymously() {
   //    var fireUser = await lastValueFrom(this.authFire.authState.pipe(first()));
   //    if (!fireUser || fireUser?.isAnonymous == false) {
   //       const auth = getAuth();
   //       await signInAnonymously(auth);
   //    }
   // }

   async ngOnInit(): Promise<void> {
      if (!this.isBrowser) {
         this.fireAuthUserLoaded = true;
         return;
      }

      this.setCountryCodeIfNeeded();

      if (sessionStorage.getItem('login_from_popup_pending_claim') === 'true') {
         sessionStorage.removeItem('login_from_popup_pending_claim');
         try {
            const isLoggedIn = this.auth.isLoggedIn();
            const onboardingClaimed = this.auth.getIsOnboardingTrialClaimed();
            const isSubscribed = await getClaimOfferIsSubscribed(isLoggedIn, SubscriptionService.subscriptionData);
            if (canShowClaimOneYearFreeOffer(isLoggedIn, onboardingClaimed, isSubscribed, true, true)) {
               this.claimOneYearFreePopupService.triggerClaimOneYearFreeOffer({
                  signupSource: 'Popup Social Login',
               });
            }
         } catch (error) {
            console.error('Error handling popup social login redirect claim check:', error);
         }
      }

      this.route.queryParams
         .pipe(takeUntil(this.unsubscribe$))
         .subscribe((params) => {
            this.handleClaimOneYearFreeQueryParam(params);

            if (params['onboarding_questions'] === 'true') {
               // if (!this.auth.isLoggedIn()) {
               //    this.auth.openAuthPopup();
               // } else {
                  // this.openOnboardingQuestionair();
               // }
            } else {
               if (this.auth.getUserId()) {
                  // this.setOnboardingInfo();
               }
            }
         });
         if (this.auth.getUserId()) {
            this.store.dispatch(loadWatchlistSymbols());
         }
         

      this.fireAuthUserLoaded = true;

      this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe(async (params) => {
         this.stripeSubscriptionStatus = params?.subscription_status;
         const duration = params?.duration ?? '';
         if (this.stripeSubscriptionStatus != undefined) {
            if (this.stripeSubscriptionStatus === 'success') {
               const { PremiumMemberGreetingPopupComponent } = await import(
                  './shared/components/premium-member-greeting-popup/premium-member-greeting-popup.component'
               );
               this.modalService.open(PremiumMemberGreetingPopupComponent, {
                  centered: true,
                  backdrop: 'static',
               });
               await this.analyticsUtils.user_subscription_completed(
                  duration ?? ''
               );
               this.dataLayerService.userSubscriptionCompletedEvent(duration);
            } else if (this.stripeSubscriptionStatus === 'cancel') {
               this.toaster.error('Subscription was not completed!');
               this.analyticsUtils.user_subscription_failed('Cancelled');
            }
            const queryParams: Params = {
               subscription_status: null,
               duration: null,
            };
            this.router.navigate([], {
               relativeTo: this.activatedRoute,
               queryParams,
               replaceUrl: true,
               queryParamsHandling: 'merge',
            });
         }
      });
   }

   private async handleClaimOneYearFreeQueryParam(params: Params) {
      if (!this.isBrowser || this.isHandlingClaimOneYearFreeQueryParam) {
         return;
      }

      if (params[this.claimOneYearFreeQueryParam] !== this.claimOneYearFreeQueryValue) {
         return;
      }

      this.isHandlingClaimOneYearFreeQueryParam = true;

      try {
         await this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
               [this.claimOneYearFreeQueryParam]: null,
            },
            replaceUrl: true,
            queryParamsHandling: 'merge',
         });

         await this.claimOneYearFreePopupService.triggerClaimOneYearFreeOffer({
            signupSource: 'Query Param Claim 1 Year Free',
         });
      } finally {
         this.isHandlingClaimOneYearFreeQueryParam = false;
      }
   }

}
